'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import {
  LayoutDashboard,
  Megaphone,
  FileText,
  Mic,
  Users,
  BarChart2,
  CreditCard,
  Settings,
  ShieldCheck,
} from 'lucide-react';
import { usersApi } from '@/lib/api';

const NAV_ITEMS = [
  { label: 'Dashboard',  href: '/dashboard',   icon: LayoutDashboard },
  { label: 'Campaigns',  href: '/campaigns',   icon: Megaphone },
  { label: 'Templates',  href: '/templates',   icon: FileText },
  { label: 'Voices',     href: '/voices',      icon: Mic },
  { label: 'Recipients', href: '/recipients',  icon: Users },
  { label: 'Reports',    href: '/reports',     icon: BarChart2 },
] as const;

const BOTTOM_ITEMS = [
  { label: 'Billing',   href: '/billing',  icon: CreditCard },
  { label: 'Settings',  href: '/settings', icon: Settings },
] as const;

function NavItem({
  href,
  icon: Icon,
  label,
  active,
}: {
  href: string;
  icon: React.ElementType;
  label: string;
  active: boolean;
}) {
  return (
    <Link
      href={href}
      className={[
        'flex items-center gap-3 px-3 py-2 rounded-[var(--radius-md)]',
        'text-[length:var(--text-sm)] font-[var(--weight-medium)]',
        'transition-colors duration-[var(--duration-fast)]',
        active
          ? 'bg-[var(--color-brand-subtle)] text-[var(--color-brand)]'
          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]',
      ].join(' ')}
    >
      <Icon size={16} strokeWidth={1.75} className="shrink-0" />
      <span>{label}</span>
    </Link>
  );
}

export function Sidebar() {
  const pathname = usePathname();

  return (
    <aside
      className="flex flex-col w-[var(--sidebar-width)] shrink-0 border-r border-[var(--color-border)] bg-[var(--color-bg-subtle)] h-screen sticky top-0"
    >
      {/* Logo */}
      <div className="flex items-center gap-2 px-4 h-[var(--header-height)] border-b border-[var(--color-border)] shrink-0">
        <span
          className="font-[var(--weight-bold)] text-[length:var(--text-md)]"
          style={{ color: 'var(--color-brand)' }}
        >
          PersonalTota
        </span>
      </div>

      {/* Main nav */}
      <nav className="flex-1 overflow-y-auto p-3 flex flex-col gap-1">
        {NAV_ITEMS.map(({ href, icon, label }) => (
          <NavItem
            key={href}
            href={href}
            icon={icon}
            label={label}
            active={pathname === href || pathname.startsWith(href + '/')}
          />
        ))}
      </nav>

      {/* Divider */}
      <div className="mx-3 border-t border-[var(--color-border)]" />

      {/* Bottom nav */}
      <div className="p-3 flex flex-col gap-1 shrink-0">
        {BOTTOM_ITEMS.map(({ href, icon, label }) => (
          <NavItem
            key={href}
            href={href}
            icon={icon}
            label={label}
            active={pathname === href}
          />
        ))}
      </div>
    </aside>
  );
}
